// Release 1.5.0 phone scan — the web copy of O-R57 (Domain\Rules\Phone\ScanCodeRules.cs). The server resolves every
// code when the phone is online; this copy reads the code on the phone so it can say "that is not a work order" at
// once, and so a work order already on the phone can still be opened while offline. Same synthetic cases as
// ScanCodeRulesTests (scanCode.test.ts).

export type ScanCodeKind = "invalid" | "labor" | "work-order";

export interface ScanCode {
  kind: ScanCodeKind;
  job: string;
  suffix: string | null;
  sequence: number | null;
  problem: string | null;
}

export const SCAN_MAX_LENGTH = 40;
export const LABOR_PAYLOAD_LENGTH = 15;
export const BLANK_SUFFIX = "000";

const NOTHING_READ = "Nothing was read. Try again or type the number.";
const WORK_ORDER_FORM = /^(?<job>\d{3,10})(?:\s*[-|]\s*(?<suffix>[A-Za-z0-9]{1,4})|\s+(?<suffix2>[A-Za-z0-9]{1,4}))?$/;

function refused(problem: string): ScanCode {
  return { kind: "invalid", job: "", suffix: null, sequence: null, problem };
}

function trimChars(text: string, chars: string): string {
  let start = 0;
  let end = text.length;
  while (start < end && chars.includes(text[start]!)) start++;
  while (end > start && chars.includes(text[end - 1]!)) end--;
  return text.slice(start, end);
}

export function parseScanCode(raw: string | null | undefined): ScanCode {
  if (raw == null) return refused(NOTHING_READ);
  const text = trimChars(trimChars(raw, "\r\n\t\0"), "*");
  if (text.trim().length === 0) return refused(NOTHING_READ);
  if (text.length > SCAN_MAX_LENGTH) return refused("That code is too long to be a work order or a router label.");

  const upper = text.toUpperCase();
  if (upper.length === LABOR_PAYLOAD_LENGTH && /^\d{6}$/.test(upper.slice(9))) {
    const job = upper.slice(0, 6).trimEnd();
    if (job.length === 0 || !/^[A-Z0-9.-]+$/.test(job)) return refused("The job number on that label could not be read. Try again or type it.");
    const suffix = upper.slice(6, 9).trimEnd();
    if (!/^[A-Z0-9]*$/.test(suffix)) return refused("The suffix on that label could not be read. Try again or type it.");
    const sequence = Number.parseInt(upper.slice(9), 10);
    if (sequence <= 0) return refused("The operation number on that label is 0. Try again or type the work order.");
    return { kind: "labor", job, suffix: suffix.length === 0 ? null : suffix, sequence, problem: null };
  }

  const m = WORK_ORDER_FORM.exec(text.trim());
  if (m?.groups) {
    const suffix = m.groups.suffix ?? m.groups.suffix2;
    return { kind: "work-order", job: m.groups.job!.toUpperCase(), suffix: suffix ? suffix.toUpperCase() : null, sequence: null, problem: null };
  }
  return refused("That is not a work-order number or a router labour barcode.");
}

/** The O-R1 keys to try, most likely first (a blank printed suffix is GSS's 000, else blank). */
export function candidateKeys(code: ScanCode): string[] {
  if (code.kind === "invalid") return [];
  if (code.suffix) return [`${code.job}|${code.suffix}`];
  return code.kind === "labor" ? [`${code.job}|${BLANK_SUFFIX}`, `${code.job}|`] : [];
}

/** The work-order page, with the operation to show when a labour barcode named one. */
export function scanPagePath(workOrderKey: string, sequence: number | null): string {
  const path = `/work-orders/${encodeURIComponent(workOrderKey)}`;
  return sequence != null ? `${path}?op=${sequence}` : path;
}

/** True when an operation's stored sequence (any zero padding) is the scanned operation number. */
export function sameOperation(stored: string | number | null | undefined, scanned: number): boolean {
  if (stored == null) return false;
  const text = String(stored).trim();
  if (!/^\d+$/.test(text)) return false;
  return Number.parseInt(text, 10) === scanned;
}

/** A work-order page parameter ("600501|000", "600501-000" or "600501") as the O-R1 key JOB|SUFFIX (the API's reading). */
export function workOrderKeyFromParam(param: string): string {
  const p = param.trim().toUpperCase();
  if (p.includes("|")) {
    const [job, suffix] = p.split("|", 2);
    return `${job!.trim()}|${(suffix ?? "").trim()}`;
  }
  const dash = p.lastIndexOf("-");
  if (dash > 0 && dash < p.length - 1) return `${p.slice(0, dash).trim()}|${p.slice(dash + 1).trim()}`;
  return `${p}|${BLANK_SUFFIX}`;
}

/** The page parameters a work order may have been opened with (search links use JOB|SUFFIX, others JOB-SUFFIX). */
export function routeFormsOfKey(key: string): string[] {
  const [job, suffix] = key.split("|", 2);
  const forms = [key, `${job}-${suffix ?? ""}`];
  if (suffix === BLANK_SUFFIX) forms.push(job!);
  return forms;
}